import React from "react";
import { Head } from "@inertiajs/react";
import { Grid, Stack, Group, Button } from "@mantine/core";
import PageShell from "../../Components/Layout/PageShell";
import PageBackgroundPhoto from "../../Components/Layout/PageBackgroundPhoto";
import { Heading1, DimmedText, MainText } from "../../Components/UI/Typography";
import { Card } from "../../Components/UI/Card";
import { Badge } from "../../Components/UI/Badge";
import GigCard from "../../Components/GigCard";
import { LatestPostsStrip } from "../../Components/LatestPostsStrip";
import {
  getPublishedGigs,
  getAllGigTags,
  getGigsByTag,
} from "../../data/gallery";

export default function CaptureIndex() {
  const [activeTag, setActiveTag] = React.useState<string | null>(null);

  const allTags = getAllGigTags();
  const gigs = activeTag ? getGigsByTag(activeTag) : getPublishedGigs();

  return (
    <>
      <Head>
        <title>Capture | Ogarocious</title>
        <meta
          name="description"
          content="Photography and videography — events, concerts, festivals, and live experiences captured through the lens."
        />
        <meta property="og:title" content="Capture | Ogarocious" />
        <meta
          property="og:description"
          content="Photography and videography — events, concerts, festivals, and live experiences."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`${import.meta.env.VITE_SITE_URL || ""}/media`}
        />
        <meta
          property="og:image"
          content={import.meta.env.VITE_OG_IMAGE_URL || ""}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Capture | Ogarocious" />
        <meta
          name="twitter:description"
          content="Photography and videography — events, concerts, festivals, live experiences."
        />
        <meta
          name="twitter:image"
          content={import.meta.env.VITE_OG_IMAGE_URL || ""}
        />
      </Head>

      <PageShell
        variant="section"
        extraBackgrounds={
          <PageBackgroundPhoto src={import.meta.env.VITE_MEDIA_PHOTO_URL} />
        }
      >
        <Stack gap={48}>
            {/* ── Header ── */}
            <Stack gap="md" align="center">
              <Heading1 variant="shadow">Capture</Heading1>
              <DimmedText
                size="lg"
                style={{ textAlign: "center", maxWidth: "clamp(280px, 90vw, 520px)" }}
              >
                Concerts, festivals, meetups, and live experiences captured
                through the lens.
              </DimmedText>
            </Stack>

            {/* ── Bio ── */}
            <Card padding="xl" radius="md">
              <Stack gap="md">
                <MainText style={{ lineHeight: 1.75 }}>
                  I started shooting to document the dance community I was building — and it grew from there. Event photography is where I'm strongest: the energy of a live moment, the light of a concert, the expression mid-movement that disappears in a frame. I shoot events, concerts, and the people that make up Austin's creative scene with the same systems thinking I bring to code — anticipate, position, execute.
                </MainText>
                <MainText style={{ lineHeight: 1.75 }}>
                  On the video side I produce motion graphics, music visualizations, and educational content. Most of my work lives at the intersection of music and movement.
                </MainText>
              </Stack>
            </Card>

            {/* ── Tag Filters ── */}
            {allTags.length > 0 && (
              <Group justify="center" gap="xs" wrap="wrap">
                <Button
                  variant={activeTag === null ? "gradient" : "subtle"}
                  color={activeTag === null ? undefined : "gray"}
                  size="xs"
                  radius="xl"
                  onClick={() => setActiveTag(null)}
                >
                  All
                </Button>
                {allTags.map((tag) => (
                  <Badge
                    key={tag}
                    variant={activeTag === tag ? "tools" : "light"}
                    size="lg"
                    radius="xl"
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      setActiveTag(activeTag === tag ? null : tag)
                    }
                  >
                    {tag}
                  </Badge>
                ))}
              </Group>
            )}

            {/* ── Gig Grid ── */}
            {gigs.length > 0 ? (
              <Grid>
                {gigs.map((gig) => (
                  <Grid.Col
                    key={gig.slug}
                    span={{ base: 12, sm: 6, lg: 4 }}
                  >
                    <GigCard gig={gig} />
                  </Grid.Col>
                ))}
              </Grid>
            ) : (
              <Stack align="center" py={60} gap="md">
                <DimmedText size="lg">
                  No gigs found for &ldquo;{activeTag}&rdquo;.
                </DimmedText>
                <Button
                  variant="subtle"
                  color="teal"
                  onClick={() => setActiveTag(null)}
                >
                  Clear filter
                </Button>
              </Stack>
            )}

            {/* ── Latest Posts ── */}
            <LatestPostsStrip pillar="media" />
        </Stack>
      </PageShell>
    </>
  );
}
